import React from 'react';
import {
  Box,
  Grid,
  Typography,
} from '@mui/material';
import {
  AttachMoney,
  People,
  Receipt,
  TrendingUp,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import ResponsiveCard from '../components/Layout/ResponsiveCard';
import type { DonationStats } from '../types';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}> = ({ title, value, icon, color = 'primary' }) => (
  <ResponsiveCard
    title={title}
    sx={{
      height: '100%',
      '& .MuiCardContent-root': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexGrow: 1,
      },
    }}
  >
    <Box>
      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
    <Box color={`${color}.main`} sx={{ ml: 2 }}>
      {icon}
    </Box>
  </ResponsiveCard>
);

const Dashboard: React.FC = () => {
  const { data: stats, isLoading, error } = useQuery<DonationStats>({
    queryKey: ['donationStats'],
    queryFn: () => apiService.getDonationStats(),
  });

  // Funzioni per tradurre i valori
  const getPaymentMethodLabel = (method: string): string => {
    const labels: { [key: string]: string } = {
      'CASH': 'Contanti',
      'CARD': 'Carta di Credito/Debito',
      'BANK_TRANSFER': 'Bonifico Bancario',
      'CHECK': 'Assegno',
      'OTHER': 'Altro'
    };
    return labels[method] || method;
  };

  const getStatusLabel = (status: string): string => {
    const labels: { [key: string]: string } = {
      'PENDING': 'In Attesa',
      'CONFIRMED': 'Confermata',
      'CANCELLED': 'Annullata',
      'REFUNDED': 'Rimborsata'
    };
    return labels[status] || status;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Caricamento statistiche...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">Errore nel caricamento delle statistiche</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Panoramica del sistema donazioni
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Totale Donazioni"
            value={stats?.totalDonations || 0}
            icon={<AttachMoney sx={{ fontSize: 40 }} />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Importo Totale"
            value={`€${Number(stats?.totalAmount || 0).toLocaleString()}`}
            icon={<TrendingUp sx={{ fontSize: 40 }} />}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Importo Medio"
            value={`€${Number(stats?.averageAmount || 0).toFixed(2)}`}
            icon={<People sx={{ fontSize: 40 }} />}
            color="info"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Questo Mese"
            value={stats?.thisMonth?.donations || 0}
            icon={<Receipt sx={{ fontSize: 40 }} />}
            color="warning"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ResponsiveCard title="Donazioni per Metodo di Pagamento">
              {stats?.byPaymentMethod && (
                <Box>
                  {Object.entries(stats.byPaymentMethod).map(([method, count]) => (
                    <Box key={method} display="flex" justifyContent="space-between" mb={1}>
                      <Typography>{getPaymentMethodLabel(method)}</Typography>
                      <Typography fontWeight="bold">{count}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </ResponsiveCard>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ResponsiveCard title="Donazioni per Stato">
              {stats?.byStatus && (
                <Box>
                  {Object.entries(stats.byStatus).map(([status, count]) => (
                    <Box key={status} display="flex" justifyContent="space-between" mb={1}>
                      <Typography>{getStatusLabel(status)}</Typography>
                      <Typography fontWeight="bold">{count}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </ResponsiveCard>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <ResponsiveCard title="Riepilogo Mensile">
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Donazioni questo mese: <strong>{stats?.thisMonth?.donations || 0}</strong>
                </Typography>
                <Typography variant="body1">
                  Importo questo mese: <strong>€{stats?.thisMonth?.amount?.toLocaleString() || 0}</strong>
                </Typography>
              </Box>
            </ResponsiveCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;
